/**
 * The scene-manager module serves as an entry point to all scene-related activities, from initialization
 * to access to objects to anything else.
 *
 * The starter version of the scene manager is adapted from the introductory code provided by three.js.
 */
import { Scene, PerspectiveCamera, WebGLRenderer, AmbientLight, DirectionalLight, Vector3, Group } from 'three'
import Star from '../cast/star'
import Earth from '../cast/earth'
import Moon from '../cast/moon'
import Laser from '../cast/laser'
import Kraken from '../cast/kraken'
import EnemyShip from '../cast/enemyShip'
import HealthPotion from '../cast/healthpotion'
import Pylon from '../cast/pylon'
import Score from '../UI/score'
import Healthbar from '../UI/healthbar'
import Restart from '../UI/restart'
import { setOpacity } from '../utilities/utilities'
import Spaceship from '../cast/spaceship'

const SHIP_DAMAGE = -34 //damage ship does per hit, 34 takes 3 hits to destroy an enemy
const ENEMY_DAMAGE = -5 //damage enemy does per hit, 10 takes 10 hits to destroy the ship
const SHIP_SHOOT_COOLDOWN = 15 //frames after ship shoots before it can shoot again
const ENEMY_SHOOT_COOLDOWN = 75 //frames after ship shoots before it can shoot again
const HIT_COOLDOWN = 10 //frames after object is damaged before it can take damage again
const POINTS_PER_ENEMY = 100 //number of points player gets after destroying an enemy
const POINTS_PER_PYLON = 1000 //number of points player gets after destroying a pylon
const POTION_AMOUNT = 50 //how much health the potion heals

const createDemoUniverse = ({ fieldOfView, width, height, nearPlane, farPlane }) => {
  const boundary = { x: (width / 2) * 0.03, y: (height / 2) * 0.03 }
  let starDensity = 50
  let planetDensity = 2
  let enemyChance = 250
  let healthChance = 1000
  let pylonChance = 1500
  let keyMap = {}
  let background = new Set()
  let enemies = new Set()
  let shipLasers = new Set()
  let enemyLasers = new Set()
  let healthItems = new Set()
  let pylons = new Set()
  let score = 0
  let gameOver = false
  let counter = 0
  let pauseDelay = false

  const scene = new Scene()
  const camera = new PerspectiveCamera(fieldOfView, width / height, nearPlane, farPlane)

  const renderer = new WebGLRenderer()
  renderer.setSize(width, height)

  scene.add(new AmbientLight('white', 0.4))
  const directionalLight = new DirectionalLight('white', 1)
  directionalLight.position.set(-3, 3, 3)
  directionalLight.target.position.set(0, 0, 0)
  scene.add(directionalLight)
  scene.add(directionalLight.target)

  const UI = new Group()
  scene.add(UI)

  const updateScore = amount => {
    score += amount
    UI.remove(UI.score)
    new Score(UI, score, camera)
  }
  updateScore(0)

  UI.healthbar = new Healthbar()
  UI.add(UI.healthbar.group)

  let ship = new Spaceship(boundary)
  scene.add(ship.group)
  ship.group.rotation.z = -Math.PI / 2

  const refreshParticle = (object, start = false) => {
    const minZ = -50
    const radius = object.type === 'planet' ? 2 + Math.random() * 3 : 0.1 + Math.random() / 8
    const z = Math.random() * minZ - radius
    const y = (Math.random() * boundary.y * 2 - boundary.y) * (3 + z / minZ)
    const stop = boundary.x * (3 + z / minZ) + radius
    const x = stop - (start ? Math.random() * stop * 2 : 0)
    object.group.position.set(x, y, z)
    object.group.scale.set(radius, radius, radius)
    object.radius = radius
    object.stop = -stop
  }

  const newPlanet = number => {
    const planet = number === 0 ? new Earth() : new Moon()
    scene.add(planet.group)
    background.add(planet)
    refreshParticle(planet, true)
  }
  for (let i = 0; i < planetDensity; i++) {
    newPlanet(i)
  }

  const newStar = () => {
    const star = new Star()
    scene.add(star.group)
    background.add(star)
    refreshParticle(star, true)
  }
  for (let i = 0; i < starDensity; i++) {
    newStar()
  }

  let action = true

  const newEnemy = () => {
    const r = Math.random()
    const radius = r > 0.6 ? 1 : 1.4
    const x = boundary.x * 2 + radius
    const y = Math.random() * boundary.y * 2 - boundary.y
    const enemy = r > 0.6 ? new Kraken(radius, { x, y, z: 0 }, -x) : new EnemyShip(radius, { x, y, z: 0 }, -x)
    scene.add(enemy.group)
    enemies.add(enemy)
  }

  const newHealthItem = () => {
    const radius = 0.3
    const x = boundary.x * 2 + radius
    const y = Math.random() * boundary.y * 2 - boundary.y
    const healthpotion = new HealthPotion(radius, { x, y, z: 0 }, -x)
    scene.add(healthpotion.group)
    healthItems.add(healthpotion)
  }

  const newPylon = () => {
    const radius = 0.7
    const x = boundary.x * 2 + radius
    const y = Math.random() * boundary.y * 2 - boundary.y
    const pylon = new Pylon(radius, { x, y, z: 0 }, -x)
    scene.add(pylon.group)
    pylons.add(pylon)
  }

  const shoot = craft => {
    const radius = 0.2
    let { x, y, z } = craft.group.position
    let t = craft === ship
    x += (craft.radius + radius) * (t ? 1.5 : -1.5)
    const xVel = craft.velocity.x + (t ? 0.8 : -0.5)
    const stop = (boundary.x + radius) * (t ? 1 : -1)
    const laser = new Laser(t ? 'cyan' : 'red', radius, { x, y, z }, xVel, stop)
    scene.add(laser.group)
    if (t) {
      shipLasers.add(laser)
      craft.cooldown.shoot = SHIP_SHOOT_COOLDOWN
    } else {
      enemyLasers.add(laser)
      craft.cooldown.shoot = ENEMY_SHOOT_COOLDOWN
    }
  }

  const updateShip = () => {
    ship.move()
    ship.updateCooldowns()
  }

  const update = (set, enemy = false, shipCollision = false, enemyCollision = false) => {
    set.forEach(o => {
      o.move(ship.group.position)
      if (enemy) {
        if (o.type === 'kraken') {
          o.animate(counter)
        } else if (o.type === 'ship' && o.cooldown.shoot === 0) {
          shoot(o)
          o.cooldown.shoot = ENEMY_SHOOT_COOLDOWN
        }
        o.updateCooldowns()
      }
      let collision = false
      if (shipCollision) {
        collision = checkCollision(o, ship)
        if (collision) {
          if (o.type === 'healthPotion') {
            updateHealth(ship, POTION_AMOUNT, HIT_COOLDOWN)
          } else {
            updateHealth(ship, ENEMY_DAMAGE, HIT_COOLDOWN)
            if (ship.health === 0) {
              endGame()
            }
          }
          if (!enemy) {
            remove(set, o)
          }
        }
      }
      if (enemyCollision) {
        let targets = new Set([...enemies, ...pylons])
        targets.forEach(t => {
          collision = checkCollision(o, t)
          if (collision) {
            updateHealth(t, SHIP_DAMAGE, HIT_COOLDOWN)
            if (t.health === 0) {
              remove(t.type === 'pylon' ? pylons : enemies, t)
              updateScore(t.type === 'pylon' ? POINTS_PER_PYLON : POINTS_PER_ENEMY)
            }
            remove(set, o)
          }
        })
      }
      if ((!enemyCollision && o.group.position.x <= o.stop) || (enemyCollision && o.group.position.x >= o.stop)) {
        if (o.type === 'star' || o.type === 'planet') {
          refreshParticle(o)
        } else {
          remove(set, o)
        }
      }
    })
  }

  const checkCollision = (a, b) => {
    if ((a.cooldown && a.cooldown.hit > 0) || (b.cooldown && b.cooldown.hit > 0)) {
      return false
    }
    return getDistance(a, b) <= a.radius + b.radius
  }

  const getDistance = (a, b) => {
    return (
      Math.sqrt(
        Math.pow(a.group.position.x - b.group.position.x, 2) + Math.pow(a.group.position.y - b.group.position.y, 2)
      ) -
      a.radius / 2 -
      b.radius / 2
    )
  }

  const updateHealth = (object, amount, cooldown) => {
    if (object.cooldown.hit === 0) {
      object.health = Math.min(Math.max(object.health + amount, 0), 100)
      object.cooldown.hit = cooldown
      setOpacity(object, 0.2)
      if (object === ship) {
        UI.healthbar.update(ship.health)
      }
    }
  }

  const remove = (set, object) => {
    scene.remove(object.group)
    set.delete(object)
  }

  const toggleAction = () => {
    action = !action
    pauseDelay = true
  }

  const getAction = () => {
    return action
  }

  const getGameOver = () => {
    return gameOver
  }

  const updateCamera = () => {
    camera.rotation.set((0.2 * ship.group.position.y) / boundary.y, (0.2 * -ship.group.position.x) / boundary.x, 0)
    UI.position.set(camera.position.x, camera.position.y, camera.position.z)
    UI.rotation.set(camera.rotation.x, camera.rotation.y, camera.rotation.z)
  }

  const endGame = () => {
    scene.remove(ship.group)
    new Restart(UI, score, camera)
    gameOver = true
  }

  const restart = () => {
    UI.remove(UI.restart)
    ship = new Spaceship(boundary)
    scene.add(ship.group)
    ship.group.rotation.z = -Math.PI / 2
    enemies.forEach(e => {
      remove(enemies, e)
    })
    shipLasers.forEach(e => {
      remove(shipLasers, e)
    })
    enemyLasers.forEach(e => {
      remove(enemyLasers, e)
    })
    healthItems.forEach(e => {
      remove(healthItems, e)
    })
    pylons.forEach(e => {
      remove(pylons, e)
    })
    score = 0
    updateScore(0)
    updateHealth(ship, 0, 1)
    gameOver = false
  }

  const animate = () => {
    window.requestAnimationFrame(animate)
    renderer.render(scene, camera)
    getInput()
    if (!action) {
      return
    }

    updateCamera()
    updateShip()
    update(background)
    update(enemies, true, !gameOver)
    update(shipLasers, false, false, true)
    update(enemyLasers, false, !gameOver)
    update(healthItems, false, !gameOver)
    update(pylons, true)

    counter++
    const random = Math.random()
    if (Math.floor(random * enemyChance) === 0) {
      newEnemy()
    }
    if (Math.floor(random * healthChance) === 0) {
      newHealthItem()
    }
    if (Math.floor(random * pylonChance) === 0) {
      newPylon()
    }
  }

  const vector = new Vector3()
  const getInput = () => {
    if (keyMap[38] | keyMap[87]) {
      if (action) {
        if (ship.cooldown.spin === 0 && ship.velocity.y < (-ship.maxVelocity.y * 2) / 3) {
          ship.cooldown.spin = Math.PI * 2
        }
        ship.accelerate(0, 0.01)
      } else {
        camera.position.add(camera.getWorldDirection(vector))
      }
    }
    if (keyMap[40] | keyMap[83]) {
      if (action) {
        if (ship.cooldown.spin === 0 && ship.velocity.y > (ship.maxVelocity.y * 2) / 3) {
          ship.cooldown.spin = -Math.PI * 2
        }
        ship.accelerate(0, -0.01)
      } else {
        camera.position.sub(camera.getWorldDirection(vector))
      }
    }
    if (keyMap[37] | keyMap[65]) {
      if (action) {
        ship.accelerate(-0.01, 0)
      } else {
        camera.rotateY(Math.PI / 2)
        camera.position.add(camera.getWorldDirection(vector))
        camera.rotateY(-Math.PI / 2)
      }
    }
    if (keyMap[39] | keyMap[68]) {
      if (action) {
        ship.accelerate(0.01, 0)
      } else {
        camera.rotateY(Math.PI / 2)
        camera.position.sub(camera.getWorldDirection(vector))
        camera.rotateY(-Math.PI / 2)
      }
    }
    if (keyMap[32] | keyMap[73]) {
      if (action && ship.cooldown.shoot === 0) {
        shoot(ship)
        ship.cooldown.shoot = SHIP_SHOOT_COOLDOWN
      }
    }
    if (keyMap[80]) {
      if (!pauseDelay) {
        toggleAction()
        camera.position.set(0, 0, 15)
      }
    } else {
      pauseDelay = false
    }
  }

  return {
    keyMap,
    camera,
    renderer,
    animate,
    toggleAction,
    getAction,
    getGameOver,
    restart
  }
}

export { createDemoUniverse }
